import React, { memo, useMemo } from "react";
import { RowClickedEvent, RowSelectedEvent, GridReadyEvent } from "ag-grid-community";
import { useHistory, useLocation } from "react-router-dom";
import { Grid, AgGrid, AgGridReact, Flex } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { ComplianceCaseStatus } from "~/graphql/supergraphEnums";
import { useGetProgramsQuery } from "~/graphql/supergraphHooks";
import { LoadingCellRenderer } from "~/pages/Compliance/Common/LoadingCellRenderer";
import NoRowsOverlay from "~/pages/Compliance/Common/NoRowsOverlay";
import { ComplianceGraphqlDataSource } from "~/pages/Compliance/ComplianceTable/complianceGraphqlDataSource";
import ProgramRenderer from "../CompliancePositionsTable/ProgramRenderer";
import {
  useAmlLuxFeatureFlag,
  useComplianceEntityOnboardingEnabled,
  useDocumentRefreshFeatureFlag,
} from "../flags";
import { dashboardContainer } from "../styles_vanilla.css";
import { isInternalEscalationSource } from "../utils/constants";
import { CaseNameRenderer } from "./CaseNameRenderer";
import { CasePositionRenderer } from "./CasePositionRenderer";
import { CaseStatusRenderer } from "./CaseStatusRenderer";
import { CaseTypeRenderer } from "./CaseTypeRenderer";
import {
  getCaseTypeFilterOptions,
  getDefaultCaseTypeFilterValues,
  isEntityClientOnboardingCaseType,
} from "./CaseTypeRenderer/utils";
import { ClientNameRenderer } from "./ClientNameRenderer";
import { DateRenderer } from "./DateRenderer";
import { ProgramNameRenderer } from "./ProgramNameRenderer";
import { ResolutionRenderer } from "./ResolutionRenderer";
import { AGGridCellRenderParams, RowItem, TypedAGGridCellRenderParams } from "./types";
import { caseTypeValueFormatter, parseCaseIdsFromUrl, statusValueFormatter } from "./utils";

export type ComplianceTableProps = {
  handleRowSelectionChanged: (event: RowSelectedEvent) => void;
  handleOnGridReady: (event: GridReadyEvent) => void;
  handleRowClicked?: (rowData: RowClickedEvent<RowItem>) => void;
  cacheBlockSize?: number;
  paginationPageSize?: number;
};

const DEFAULT_PAGE_SIZE = 100;

export const ComplianceTable = memo(
  ({
    handleRowSelectionChanged,
    handleOnGridReady,
    cacheBlockSize = DEFAULT_PAGE_SIZE,
    paginationPageSize = DEFAULT_PAGE_SIZE,
  }: ComplianceTableProps) => {
    const history = useHistory();
    const location = useLocation();
    const caseIdsFromUrl = parseCaseIdsFromUrl(location.search);
    const documentRefreshEnabled = useDocumentRefreshFeatureFlag();
    const entityOnboardingEnabled = useComplianceEntityOnboardingEnabled();
    const amlLuxEnabled = useAmlLuxFeatureFlag();
    const caseTypeFilterFlags = useMemo(
      () => ({ documentRefreshEnabled, entityOnboardingEnabled }),
      [documentRefreshEnabled, entityOnboardingEnabled],
    );

    const complianceCaseDatasource = useMemo(
      () => new ComplianceGraphqlDataSource(caseIdsFromUrl),
      [caseIdsFromUrl],
    );

    const handleRowClicked = (rowData: RowClickedEvent<RowItem>) => {
      const target = rowData.event?.target;
      if (target instanceof Element && target.closest("button, a")) {
        return;
      }
      if (rowData.data?.caseId) {
        history.push(`${globalEnv.complianceRoute}/case/${rowData.data.caseId}`);
      }
    };

    const initialFilter = (event: AgGrid.GridReadyEvent) => {
      event.api.getFilterInstance("status")?.setModel({
        values: Object.values(ComplianceCaseStatus).filter(
          (status) =>
            ![
              ComplianceCaseStatus.Closed,
              ComplianceCaseStatus.PendingSubmission,
              ComplianceCaseStatus.NotReady,
            ].includes(status),
        ),
      });
      event.api.getFilterInstance("caseType")?.setModel({
        values: filteredCaseTypes,
      });
      event.api.onFilterChanged();
    };

    const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
      autoSizeStrategy: {
        type: "fitGridWidth",
      },
      rowHeight: 61,
      suppressCellFocus: true,
      rowSelection: "multiple",
      suppressRowClickSelection: true, // Prevents row deselection when clicking on a row
      statusBar: {
        statusPanels: [
          { statusPanel: "agTotalRowCountComponent" },
          { statusPanel: "agSelectedRowCountComponent" },
        ],
      },
      rowModelType: "serverSide",
      serverSideDatasource: complianceCaseDatasource,
      onFirstDataRendered: initialFilter,
      getRowId: (params) => params.data.caseId?.toString(),
    };
    const programsResult = useGetProgramsQuery();
    const programs = programsResult.data?.programsAssignableToEntities?.map(
      (program) => program.name,
    );
    const programTypeToName = useMemo(
      () =>
        Object.fromEntries(
          (programsResult.data?.programsAssignableToEntities ?? []).map((p) => [
            p.programType,
            p.name,
          ]),
        ),
      [programsResult.data],
    );
    const programTypes = useMemo(
      () => programsResult.data?.programsAssignableToEntities?.map((p) => p.programType),
      [programsResult.data],
    );

    const filteredCaseTypes = useMemo(
      () => getDefaultCaseTypeFilterValues(caseTypeFilterFlags),
      [caseTypeFilterFlags],
    );
    const caseTypeFilterOptions = useMemo(
      () => getCaseTypeFilterOptions(caseTypeFilterFlags),
      [caseTypeFilterFlags],
    );

    const colDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
      {
        field: "caseName",
        headerName: "Case name",
        cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
          <CaseNameRenderer params={params} />
        ),
        checkboxSelection: true,
        headerCheckboxSelection: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          // BE doesn't support multiple AND conditions for the same field, so disable it for now
          maxNumConditions: 1,
        },
        flex: 2,
        pinned: "left",
        minWidth: 150,
      },
      amlLuxEnabled
        ? {
            colId: "programTypes",
            field: "casePrograms",
            headerName: "Program",
            cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
              <ProgramRenderer value={params.data?.casePrograms ?? []} />
            ),
            filter: "agSetColumnFilter",
            filterParams: {
              values: programTypes,
              valueFormatter: (params: { value: string }) =>
                programTypeToName[params.value] ?? params.value,
            },
            flex: 1,
            minWidth: 120,
          }
        : {
            field: "programName",
            headerName: "Program",
            cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
              <ProgramNameRenderer params={params} />
            ),
            filter: "agSetColumnFilter",
            filterParams: {
              values: programs,
            },
            flex: 1,
            minWidth: 120,
          },
      {
        field: "caseType",
        headerName: "Case type",
        cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
          <CaseTypeRenderer params={params} />
        ),
        filter: "agSetColumnFilter",
        filterParams: {
          values: caseTypeFilterOptions,
          valueFormatter: caseTypeValueFormatter,
        },
        flex: 1,
        minWidth: 150,
      },
      {
        field: "client",
        cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) =>
          params.value === "" ? "--" : <ClientNameRenderer params={params} />,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 1,
        },
        flex: 1,
        minWidth: 120,
      },
      {
        field: "entity",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 1,
        },
        cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) =>
          params.value === "" ? "--" : params.value,
        flex: 1,
        minWidth: 120,
      },
      {
        colId: "position",
        headerName: "Position",
        cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
          <CasePositionRenderer params={params} />
        ),
        sortable: false,
        flex: 1,
        minWidth: 160,
      },
      {
        field: "creationDate",
        headerName: "Creation date",
        cellRenderer: (params: AGGridCellRenderParams) => <DateRenderer params={params} />,
        flex: 1,
        minWidth: 130,
      },
      {
        field: "status",
        cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
          <CaseStatusRenderer
            params={{
              value: params.value as string,
            }}
          />
        ),
        filter: "agSetColumnFilter",
        filterParams: {
          values: Object.values(ComplianceCaseStatus),
          valueFormatter: statusValueFormatter,
        },
        flex: 1,
        minWidth: 120,
      },
      {
        field: "resolution",
        headerName: "Resolution",
        cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
          <ResolutionRenderer
            resolution={params.data?.resolution ?? ""}
            status={params.data?.status}
            internal={
              !!params.data &&
              (isEntityClientOnboardingCaseType(params.data.caseType) ||
                isInternalEscalationSource(params.data.sourceCaseType))
            }
          />
        ),
        sortable: false,
        flex: 1,
        minWidth: 120,
      },
      {
        field: "assignee",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 1,
        },
        flex: 1,
        minWidth: 120,
      },
    ];

    return (
      <Flex className={dashboardContainer}>
        <Grid
          gridOptions={gridOptions}
          columnDefs={colDefs}
          onRowClicked={handleRowClicked}
          suppressColumnVirtualisation={true}
          suppressRowVirtualisation={true}
          onSelectionChanged={handleRowSelectionChanged}
          onGridReady={handleOnGridReady}
          loadingCellRenderer={LoadingCellRenderer}
          noRowsOverlayComponent={NoRowsOverlay}
          noRowsOverlayComponentParams={{ message: "No cases found" }}
          // Paging
          pagination
          paginationPageSizeSelector={false}
          paginationPageSize={paginationPageSize}
          // SSRM
          rowModelType="serverSide"
          serverSideDatasource={complianceCaseDatasource}
          cacheBlockSize={cacheBlockSize}
        />
      </Flex>
    );
  },
);
ComplianceTable.displayName = "ComplianceTable";
